import { Client, deriveAccountStatus, Inventory, Invoice, isInventoryAccountUnavailableForSale, ManualAccountStatus, PurchaseOrder, resolveManualAccountStatus, SalesOrder, User, Vendor } from "@/core";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateInvoice,
  useCreatePurchaseOrderManual,
  useCreateSalesOrderManual,
  useNoteTemplates,
  useRecordStockCheck,
  useUpdateInventory,
} from "@/hooks";
import { useProductsStore } from "@/stores/products";
import { useUserStore } from "@/stores/user";
import { SalesOrderRateFields } from "./SalesOrderRateFields";
import {
  formatCurrency,
  formatStatusLabel,
  formatUserDisplayName,
  getInventoryBookingBlockDetails,
  getOrderInvoiceSummary,
  getSalesOrderErrorMessage,
  getSalesOrderOversellNotice,
  getSalesOrderShortfall,
  getSalesOrderShortfallFromError,
  isInventoryBlockedForSalesOrderLinking,
  type SalesOrderShortfall,
} from "./operations-shared";
import { SalesOrderOversellDialog } from "./SalesOrderOversellDialog";
import { formatDateOnly, timestampToDate } from "@/utils/formatting";
import { calculateOrderTotal, getPurchaseOrderTotal, getSalesOrderTotal } from "@/utils/order-total";
import { getClientSearchKeywords, getClientSelectLabel, getVendorSearchKeywords, getVendorSelectLabel } from "@/utils";
import { useEffect, useMemo, useState } from "react";

interface BaseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getToday = () => new Date().toISOString().split("T")[0];

export function InventoryWorkspacePurchaseOrderCreateSheet(
  props: Readonly<
    BaseSheetProps & {
      inventory: Inventory | null | undefined;
      vendors: Vendor[];
      onCreated?: (poId: string) => void;
    }
  >,
) {
  const { open, onOpenChange, inventory, vendors, onCreated } = props;
  const createMutation = useCreatePurchaseOrderManual();
  const [vendorId, setVendorId] = useState("");
  const [source, setSource] = useState<"CRM" | "Broker" | "Friend" | "Other">(
    "Broker",
  );
  const [amountBought, setAmountBought] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [taxCost, setTaxCost] = useState("");
  const [paymentTiming, setPaymentTiming] = useState<
    "pay_in_advance" | "pay_after" | "pay_once_booked"
  >("pay_in_advance");
  const [nextPaymentDate, setNextPaymentDate] = useState("");
  const [okToPay, setOkToPay] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    | "paypal_goods_services"
    | "paypal_friends_family"
    | "paypal_goods_services_fees"
    | "paypal"
    | "bank"
    | "zelle"
    | "card"
    | "crypto"
    | "other"
  >("paypal_goods_services");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [notes, setNotes] = useState("");
  const defaultVendorId = useMemo(() => {
    if (!inventory) {
      return "";
    }

    const normalizedOwnerName = inventory.accountOwnerName?.trim().toLowerCase();
    const normalizedAccountEmail = inventory.accountEmail?.trim().toLowerCase();
    const matchedVendor = vendors.find((vendor) => {
      const vendorLabel = getVendorSelectLabel({ vendor }).trim().toLowerCase();
      const vendorEmail = vendor.email?.trim().toLowerCase();

      return (
        (normalizedOwnerName && vendorLabel === normalizedOwnerName) ||
        (normalizedAccountEmail && vendorEmail === normalizedAccountEmail)
      );
    });

    return matchedVendor?.id || "";
  }, [inventory, vendors]);
  const totalAmount = calculateOrderTotal({
    amount: Number(buyPrice) || 0,
    taxCost: Number(taxCost) || 0,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setVendorId(defaultVendorId);
  }, [defaultVendorId, open]);

  const canSubmit =
    Boolean(inventory?.id) &&
    Boolean(vendorId) &&
    (Number(amountBought) || 0) > 0 &&
    (Number(buyPrice) || 0) >= 0 &&
    (Number(taxCost) || 0) >= 0;

  const handleSubmit = async () => {
    if (!inventory || !canSubmit) {
      return;
    }

    const result = await createMutation.mutateAsync({
      createMode: "manual",
      source,
      vendorId,
      productId: inventory.productId,
      productName: inventory.productName,
      amountBought: Number(amountBought),
      buyPrice: Number(buyPrice),
      taxCost: Number(taxCost),
      paymentTiming,
      nextPaymentDate: nextPaymentDate || undefined,
      okToPay,
      paymentMethod,
      paymentNotes: paymentNotes.trim() || undefined,
      paymentDetails: paymentDetails.trim() || undefined,
      notes: notes.trim() || undefined,
      inventoryMode: "existing",
      inventoryId: inventory.id,
    });

    setVendorId("");
    setAmountBought("");
    setBuyPrice("");
    setTaxCost("");
    setPaymentTiming("pay_in_advance");
    setNextPaymentDate("");
    setOkToPay(false);
    setPaymentMethod("paypal_goods_services");
    setPaymentNotes("");
    setPaymentDetails("");
    setNotes("");
    onOpenChange(false);
    onCreated?.(result.poId);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-full overflow-y-auto p-0 md:max-w-xl">
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>Create Purchase Order</SheetTitle>
            <SheetDescription>
              Create a purchase order already linked to this inventory.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-6 py-6 space-y-4">
          <div className="space-y-1">
            <Label>Inventory</Label>
            <Input
              value={
                inventory
                  ? `${inventory.name} · ${inventory.productName}`
                  : ""
              }
              disabled
            />
          </div>

          <div className="space-y-1">
            <Label>Vendor</Label>
            <SearchableSelect
              value={vendorId}
              onChange={setVendorId}
              placeholder="Select vendor"
              searchPlaceholder="Search vendor..."
              emptyText="No vendor found."
              options={vendors.map((vendor) => ({
                value: vendor.id,
                label: getVendorSelectLabel({ vendor }),
                keywords: getVendorSearchKeywords({ vendor }),
              }))}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Source</Label>
              <Select
                value={source}
                onValueChange={(value) =>
                  setSource(value as typeof source)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRM">CRM</SelectItem>
                  <SelectItem value="Broker">Broker</SelectItem>
                  <SelectItem value="Friend">Friend</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Payment Timing</Label>
              <Select
                value={paymentTiming}
                onValueChange={(value) =>
                  setPaymentTiming(value as typeof paymentTiming)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pay_in_advance">Paid in advance</SelectItem>
                  <SelectItem value="pay_after">Paid after flight</SelectItem>
                  <SelectItem value="pay_once_booked">Pay once booked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Amount Bought</Label>
              <Input
                value={amountBought}
                onChange={(event) => setAmountBought(event.target.value)}
                placeholder="30000"
              />
            </div>
            <div className="space-y-1">
              <Label>Buy Price</Label>
              <Input
                value={buyPrice}
                onChange={(event) => setBuyPrice(event.target.value)}
                placeholder="1200"
              />
            </div>
            <div className="space-y-1">
              <Label>Tax Cost</Label>
              <Input
                value={taxCost}
                onChange={(event) => setTaxCost(event.target.value)}
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Next Payment Date</Label>
              <Input
                type="date"
                value={nextPaymentDate}
                onChange={(event) => setNextPaymentDate(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Payment Method</Label>
              <Select
                value={paymentMethod}
                onValueChange={(value) => setPaymentMethod(value as typeof paymentMethod)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal_goods_services">PayPal - Goods and Services</SelectItem>
                  <SelectItem value="paypal_friends_family">PayPal - Friends and Family</SelectItem>
                  <SelectItem value="paypal_goods_services_fees">PayPal - Goods and Services + Fees</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>OK To Pay</Label>
            <Select
              value={okToPay ? "yes" : "no"}
              onValueChange={(value) => setOkToPay(value === "yes")}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Payment Notes</Label>
            <Textarea
              value={paymentNotes}
              onChange={(event) => setPaymentNotes(event.target.value)}
              placeholder="Optional payment notes"
            />
          </div>

          <div className="space-y-1">
            <Label>Payment Details</Label>
            <Textarea
              value={paymentDetails}
              onChange={(event) => setPaymentDetails(event.target.value)}
              placeholder="Optional payment details"
            />
          </div>

          <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm">
            Total Amount: {formatCurrency({ value: totalAmount })}
          </div>

          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional purchase notes"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={createMutation.isPending}
              loadingText="Creating..."
            >
              Create Purchase Order
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function InventoryWorkspaceSalesOrderCreateSheet(
  props: Readonly<
    BaseSheetProps & {
      inventory: Inventory | null | undefined;
      clients: Client[];
      users: User[];
      onCreated?: (soId: string) => void;
    }
  >,
) {
  const { open, onOpenChange, inventory, clients, users, onCreated } = props;
  const createMutation = useCreateSalesOrderManual();
  const { isAdmin } = useUserStore();
  const canApprove = isAdmin();
  const [clientId, setClientId] = useState("");
  const handleClientChange = (id: string) => {
    setClientId(id);
  };
  const [flightDate, setFlightDate] = useState("");
  const [bookingEndDate, setBookingEndDate] = useState("");
  const [bookingTbd, setBookingTbd] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState(getToday);
  const [soldByUserId, setSoldByUserId] = useState("");
  const [totalMilesSold, setTotalMilesSold] = useState("");
  const [revenue, setRevenue] = useState("");
  const [taxCost, setTaxCost] = useState("");
  const [transferProductId, setTransferProductId] = useState("");
  const [notes, setNotes] = useState("");
  const noteTemplatesQuery = useNoteTemplates();
  const noteTemplates = noteTemplatesQuery.data || [];
  const products = useProductsStore((state) => state.products);

  const transferProductOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: product.name,
        keywords: [product.name, product.id],
      })),
    [products],
  );

  const soldByOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: formatUserDisplayName(user.email),
        keywords: [formatUserDisplayName(user.email), user.email, user.id],
      })),
    [users],
  );

  useEffect(() => {
    if (!open || !inventory || noteTemplates.length === 0) {
      return;
    }
    // Don't overwrite text the user has already typed.
    if (notes.trim().length > 0) {
      return;
    }
    const productTemplate = noteTemplates.find(
      (template) => template.productId === inventory.productId,
    );
    const defaultTemplate = noteTemplates.find(
      (template) => !template.productId,
    );
    const matched = productTemplate || defaultTemplate;
    if (matched?.content) {
      setNotes(matched.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inventory?.id, inventory?.productId, noteTemplates]);

  const [showBlockedConfirm, setShowBlockedConfirm] = useState(false);
  const [oversellToConfirm, setOversellToConfirm] =
    useState<SalesOrderShortfall | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const shortfall = inventory
    ? getSalesOrderShortfall({
        requiredMiles: Number(totalMilesSold) || 0,
        availableMiles: inventory.availableMiles,
      })
    : null;
  // Only an oversell needs approval.
  const createsDraft = !canApprove && shortfall !== null;
  const inventoryBookingBlock = inventory
    ? getInventoryBookingBlockDetails({ inventory })
    : null;
  const inventoryBlocked = inventory
    ? isInventoryBlockedForSalesOrderLinking({ inventory })
    : false;
  const accountUnavailable = isInventoryAccountUnavailableForSale(
    inventory?.accountStatus,
  );
  const canSubmit =
    !accountUnavailable &&
    Boolean(inventory?.id) &&
    Boolean(clientId) &&
    (bookingTbd || Boolean(flightDate)) &&
    (bookingTbd || Boolean(bookingEndDate)) &&
    Boolean(soldByUserId) &&
    (Number(totalMilesSold) || 0) > 0 &&
    (Number(revenue) || 0) >= 0 &&
    (Number(taxCost) || 0) >= 0;
  const totalAmount = calculateOrderTotal({
    amount: Number(revenue) || 0,
    taxCost: Number(taxCost) || 0,
  });

  const handleSubmit = async (allowOversell = false) => {
    if (!inventory || !canSubmit) {
      return;
    }

    setSubmitError(null);

    if (canApprove && shortfall && !allowOversell) {
      setOversellToConfirm(shortfall);
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        createMode: "manual",
        allowOversell: canApprove && allowOversell,
        inventoryId: inventory.id,
        flightDate: bookingTbd ? undefined : flightDate,
        soldByUserId,
        bookingEndDate: bookingTbd ? undefined : bookingEndDate,
        bookingTbd,
        purchaseDate: purchaseDate || undefined,
        clientId,
        productId: inventory.productId,
        productName: inventory.productName,
        totalMilesSold: Number(totalMilesSold),
        revenue: Number(revenue),
        taxCost: Number(taxCost),
        transferProductId: transferProductId || undefined,
        salesChannel: "manual",
        notes: notes.trim() || undefined,
        overrideBookingBlock: inventoryBlocked ? true : false,
      });

      setClientId("");
      setFlightDate("");
      setBookingEndDate("");
      setBookingTbd(false);
      setPurchaseDate(getToday());
      setSoldByUserId("");
      setTotalMilesSold("");
      setRevenue("");
      setTaxCost("");
      setTransferProductId("");
      setNotes("");
      setShowBlockedConfirm(false);
      onOpenChange(false);
      onCreated?.(result.soId);
    } catch (error: unknown) {
      // The miles may have changed since the form loaded; let an admin decide.
      const serverShortfall = getSalesOrderShortfallFromError(error);
      if (canApprove && serverShortfall && !allowOversell) {
        setOversellToConfirm(serverShortfall);
        return;
      }
      setSubmitError(getSalesOrderErrorMessage(error));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-full overflow-y-auto p-0 md:max-w-xl">
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>Create Sell Order</SheetTitle>
            <SheetDescription>
              Create a sell order already linked to this inventory.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-6 py-6 space-y-4">
          <div className="space-y-1">
            <Label>Inventory</Label>
            <Input
              value={
                inventory
                  ? `${inventory.name} · ${inventory.productName}`
                  : ""
              }
              disabled
            />
          </div>

          {accountUnavailable ? (
            <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
              <span className="font-medium">
                This inventory's account is{" "}
                {formatStatusLabel(inventory?.accountStatus)}.
              </span>{" "}
              Sell orders can't be created from it.
            </div>
          ) : null}

          {inventoryBlocked ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span className="font-medium">This inventory is currently in use.</span>{" "}
              {inventoryBookingBlock?.tbd
                ? "Blocked indefinitely (TBD)."
                : `Blocked until ${formatDateOnly(inventoryBookingBlock?.blockedUntil) || "a later date"}.`}
            </div>
          ) : null}

          <div className="space-y-1">
            <Label>Client</Label>
            <SearchableSelect
              value={clientId}
              onChange={handleClientChange}
              placeholder="Select client"
              searchPlaceholder="Search client..."
              emptyText="No client found."
              options={clients.map((client) => ({
                value: client.id,
                label: getClientSelectLabel({ client }),
                keywords: getClientSearchKeywords({ client }),
              }))}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={bookingTbd}
              onCheckedChange={(checked) => {
                const next = checked === true;
                setBookingTbd(next);
                if (next) {
                  setFlightDate("");
                  setBookingEndDate("");
                }
              }}
            />
            <span>TBD — flight not yet scheduled (keeps inventory blocked)</span>
          </label>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <Label>Order Date</Label>
              <Input
                type="date"
                value={purchaseDate}
                onChange={(event) => setPurchaseDate(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Flight Date</Label>
              <Input
                type="date"
                value={flightDate}
                onChange={(event) => setFlightDate(event.target.value)}
                disabled={bookingTbd}
              />
            </div>
            <div className="space-y-1">
              <Label>Booking End Date</Label>
              <Input
                type="date"
                value={bookingEndDate}
                onChange={(event) => setBookingEndDate(event.target.value)}
                disabled={bookingTbd}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Sold By</Label>
            <SearchableSelect
              value={soldByUserId}
              onChange={setSoldByUserId}
              placeholder="Select sold-by name"
              searchPlaceholder="Search sold-by name..."
              emptyText="No users found."
              options={soldByOptions}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Miles Sold</Label>
              <Input
                value={totalMilesSold}
                onChange={(event) => setTotalMilesSold(event.target.value)}
                placeholder="30000"
              />
            </div>
            <SalesOrderRateFields
              milesSold={totalMilesSold}
              revenue={revenue}
              onRevenueChange={setRevenue}
            />
            <div className="space-y-1">
              <Label>Tax Cost</Label>
              <Input
                value={taxCost}
                onChange={(event) => setTaxCost(event.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1">
              <Label>Transfer Product</Label>
              <SearchableSelect
                value={transferProductId}
                onChange={setTransferProductId}
                placeholder="Select transfer product"
                searchPlaceholder="Search transfer product..."
                emptyText="No product found."
                options={transferProductOptions}
                clearable
              />
            </div>
          </div>

          <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm">
            Total Amount: {formatCurrency({ value: totalAmount })}
          </div>

          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional sell-order notes"
            />
          </div>

          {submitError ? (
            <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
              {submitError}
            </div>
          ) : null}

          {shortfall && !accountUnavailable ? (
            <div className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900">
              {getSalesOrderOversellNotice({ shortfall, canApprove })}
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button
              onClick={() => inventoryBlocked ? setShowBlockedConfirm(true) : void handleSubmit()}
              disabled={!canSubmit}
              loading={createMutation.isPending}
              loadingText="Creating..."
            >
              {createsDraft ? "Submit for Approval" : "Create Sell Order"}
            </Button>
          </div>
        </div>
      </SheetContent>

      <AlertDialog open={showBlockedConfirm} onOpenChange={setShowBlockedConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This inventory is currently in use</AlertDialogTitle>
            <AlertDialogDescription>
              {inventoryBookingBlock?.tbd
                ? "This inventory is blocked indefinitely (TBD)."
                : `This inventory is blocked until ${formatDateOnly(inventoryBookingBlock?.blockedUntil) || "a later date"}.`}{" "}
              Are you sure you want to book from this account simultaneously?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleSubmit()}>
              {createsDraft ? "Submit for Approval" : "Create Sell Order"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SalesOrderOversellDialog
        shortfall={oversellToConfirm}
        onCancel={() => setOversellToConfirm(null)}
        onConfirm={() => {
          setOversellToConfirm(null);
          void handleSubmit(true);
        }}
      />
    </Sheet>
  );
}

export function InventoryWorkspaceInvoiceCreateSheet(
  props: Readonly<
    BaseSheetProps & {
      kind: "po" | "so";
      purchaseOrders: PurchaseOrder[];
      salesOrders: SalesOrder[];
      invoices: Invoice[];
      clearedByInvoiceId: ReadonlyMap<string, number>;
      defaultOrderId?: string;
      onCreated?: (invoiceId: string) => void;
    }
  >,
) {
  const {
    open,
    onOpenChange,
    kind,
    purchaseOrders,
    salesOrders,
    invoices,
    clearedByInvoiceId,
    defaultOrderId,
    onCreated,
  } = props;
  const createInvoiceMutation = useCreateInvoice();
  const [orderId, setOrderId] = useState(defaultOrderId || "");
  const [amountDue, setAmountDue] = useState("");
  const [dueAt, setDueAt] = useState("");

  useEffect(() => {
    if (open) {
      setOrderId(defaultOrderId || "");
    }
  }, [defaultOrderId, open]);

  const orderOptions = useMemo(
    () =>
      kind === "po"
        ? purchaseOrders.map((purchaseOrder) => ({
            value: purchaseOrder.id,
            label:
              purchaseOrder.vendorDisplayName ||
              purchaseOrder.vendorEmail ||
              purchaseOrder.id.slice(0, 8),
          }))
        : salesOrders.map((salesOrder) => ({
            value: salesOrder.id,
            label:
              salesOrder.clientDisplayName ||
              salesOrder.clientEmail ||
              salesOrder.id.slice(0, 8),
          })),
    [kind, purchaseOrders, salesOrders],
  );

  const selectedPurchaseOrder = useMemo(
    () => purchaseOrders.find((purchaseOrder) => purchaseOrder.id === orderId),
    [orderId, purchaseOrders],
  );
  const selectedSalesOrder = useMemo(
    () => salesOrders.find((salesOrder) => salesOrder.id === orderId),
    [orderId, salesOrders],
  );
  const selectedOrderTotal = kind === "po"
    ? getPurchaseOrderTotal({
      buyPrice: selectedPurchaseOrder?.buyPrice || 0,
      taxCost: selectedPurchaseOrder?.taxCost,
      totalAmount: selectedPurchaseOrder?.totalAmount,
    })
    : getSalesOrderTotal({
      revenue: selectedSalesOrder?.revenue || 0,
      taxCost: selectedSalesOrder?.taxCost,
      totalAmount: selectedSalesOrder?.totalAmount,
    });
  const selectedOrderInvoiceSummary = useMemo(
    () =>
      getOrderInvoiceSummary({
        kind,
        orderId,
        invoices,
        clearedByInvoiceId,
      }),
    [clearedByInvoiceId, invoices, kind, orderId],
  );
  const remainingToInvoice = Math.max(
    0,
    selectedOrderTotal - selectedOrderInvoiceSummary.invoiced,
  );

  const canSubmit = Boolean(orderId) && (Number(amountDue) || 0) >= 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    const result = await createInvoiceMutation.mutateAsync({
      poId: kind === "po" ? orderId : undefined,
      soId: kind === "so" ? orderId : undefined,
      amountDue: Number(amountDue),
      currency: "USD",
      dueAt: dueAt || undefined,
    });

    setAmountDue("");
    setDueAt("");
    onOpenChange(false);
    onCreated?.(result.invoiceId);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-full overflow-y-auto p-0 md:max-w-lg">
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>
              Create {kind === "po" ? "Purchase" : "Sell"} Invoice
            </SheetTitle>
            <SheetDescription>
              Add an invoice directly inside this inventory workspace.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-6 py-6 space-y-4">
          <div className="space-y-1">
            <Label>{kind === "po" ? "Purchase Order" : "Sell Order"}</Label>
            <SearchableSelect
              value={orderId}
              onChange={setOrderId}
              placeholder={`Select ${kind === "po" ? "purchase" : "sell"} order`}
              searchPlaceholder="Search order..."
              emptyText="No order found."
              options={orderOptions}
            />
          </div>

          {orderId ? (
            <div className="rounded-lg border bg-muted/30 px-4 py-3">
              <div className="text-sm font-medium">
                {kind === "po" ? "Purchase order" : "Sell order"} financials
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Order Total
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrency({ value: selectedOrderTotal })}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Invoiced
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrency({ value: selectedOrderInvoiceSummary.invoiced })}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Cleared
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrency({ value: selectedOrderInvoiceSummary.cleared })}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Remaining To Invoice
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrency({ value: remainingToInvoice })}
                  </div>
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                {selectedOrderInvoiceSummary.invoiceCount} linked invoice
                {selectedOrderInvoiceSummary.invoiceCount === 1 ? "" : "s"}.
                All amounts are in USD.
              </div>
            </div>
          ) : null}

          <div className="space-y-1">
            <Label>Amount Due (USD)</Label>
            <Input
              value={amountDue}
              onChange={(event) => setAmountDue(event.target.value)}
              placeholder="0"
            />
          </div>

          <div className="space-y-1">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={createInvoiceMutation.isPending}
              loadingText="Creating..."
            >
              Create Invoice
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function InventoryWorkspaceInventoryEditSheet(
  props: Readonly<
    BaseSheetProps & {
      inventory: Inventory | null | undefined;
      vendors: Vendor[];
    }
  >,
) {
  const { open, onOpenChange, inventory, vendors } = props;
  const updateInventoryMutation = useUpdateInventory();
  const [name, setName] = useState("");
  const [status, setStatus] = useState<
    "draft" | "active" | "depleted" | "closed" | "blocked" | "archived"
  >("draft");
  // Admin-controlled base status; the effective accountStatus (incl. on_hold /
  // written_off / needs_reset) is derived by the recompute trigger.
  const [manualAccountStatus, setManualAccountStatus] =
    useState<ManualAccountStatus>("active");
  const [accountOwnerName, setAccountOwnerName] = useState("");
  const [ownerVendorId, setOwnerVendorId] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [statusTierName, setStatusTierName] = useState("");
  const [statusTierPercentage, setStatusTierPercentage] = useState("");
  const [dob, setDob] = useState("");
  const [ipLocation, setIpLocation] = useState("");
  const [twoFaStatus, setTwoFaStatus] = useState<
    | "none"
    | "email_forwarding"
    | "otp_login"
    | "otp_booking"
    | "otp_authenticator"
    | "ah_to_book"
    | "no_need_to_check"
    | "agent"
  >("none");
  const [cardTypeTag, setCardTypeTag] = useState("");
  const [gologinProfileId, setGologinProfileId] = useState("");
  const [username, setUsername] = useState("");
  const [expirationInfo, setExpirationInfo] = useState("");
  const [notes, setNotes] = useState("");
  const vendorOptions = useMemo(
    () =>
      [...vendors]
        .sort((left, right) =>
          getVendorSelectLabel({ vendor: left }).localeCompare(
            getVendorSelectLabel({ vendor: right }),
          ),
        )
        .map((vendor) => ({
          value: vendor.id,
          label: getVendorSelectLabel({ vendor }),
          keywords: getVendorSearchKeywords({ vendor }),
        })),
    [vendors],
  );

  useEffect(() => {
    if (!open || !inventory) {
      return;
    }

    setName(inventory.name);
    setStatus(inventory.status);
    setManualAccountStatus(
      resolveManualAccountStatus({
        manualAccountStatus: inventory.manualAccountStatus,
        accountStatus: inventory.accountStatus,
      }),
    );
    setAccountOwnerName(inventory.accountOwnerName || "");
    setOwnerVendorId(
      vendorOptions.find(
        (option) => option.label === (inventory.accountOwnerName || ""),
      )?.value || "",
    );
    setAccountEmail(inventory.accountEmail || "");
    setAccountPassword(inventory.accountPassword || "");
    setPaypalEmail(inventory.paypalEmail || "");
    setStatusTierName(inventory.statusTier?.name || "");
    setStatusTierPercentage(
      inventory.statusTier ? String(inventory.statusTier.percentage) : "",
    );
    setDob(formatDateOnly(timestampToDate(inventory.dob)) || "");
    setIpLocation(inventory.ipLocation || "");
    setTwoFaStatus((inventory.twoFaStatus as typeof twoFaStatus) ?? "none");
    setCardTypeTag(inventory.cardTypeTag || "");
    setGologinProfileId(inventory.gologinProfileId || "");
    setUsername(inventory.username || "");
    setExpirationInfo(inventory.expirationInfo || "");
    setNotes(inventory.notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inventory?.id]);

  const handleOwnerVendorChange = (vendorId: string) => {
    const selectedVendor = vendors.find((vendor) => vendor.id === vendorId);

    setOwnerVendorId(vendorId);
    setAccountOwnerName(
      selectedVendor ? getVendorSelectLabel({ vendor: selectedVendor }) : "",
    );
    setAccountEmail(selectedVendor?.email || "");
  };

  const handleSubmit = async () => {
    if (!inventory) {
      return;
    }

    await updateInventoryMutation.mutateAsync({
      id: inventory.id,
      data: {
        name: name.trim(),
        status,
        manualAccountStatus,
        // Derive the effective status inline so it updates immediately on save;
        // editing credentials / manual base doesn't change miles or CPM, so the
        // stored accountValue / buyCpm are still valid inputs. The recompute
        // trigger reconciles as a backstop.
        accountStatus: deriveAccountStatus({
          manualAccountStatus,
          problemStatus: inventory.problemStatus,
          accountValue: inventory.accountValue ?? 0,
          availableMiles: inventory.availableMiles ?? 0,
          buyCpm: inventory.buyCpm ?? null,
          hasCredentials: Boolean(
            accountEmail.trim() && accountPassword.trim(),
          ),
        }),
        accountOwnerName: accountOwnerName.trim() || undefined,
        accountEmail: accountEmail.trim() || undefined,
        accountPassword: accountPassword.trim() || undefined,
        paypalEmail: paypalEmail.trim() || undefined,
        statusTier:
          statusTierName.trim() && Number.isFinite(Number(statusTierPercentage))
            ? {
                name: statusTierName.trim(),
                percentage: Number(statusTierPercentage),
              }
            : undefined,
        dob: dob ? new Date(dob) : undefined,
        ipLocation: ipLocation.trim() || undefined,
        twoFaStatus,
        cardTypeTag: cardTypeTag.trim() || undefined,
        gologinProfileId: gologinProfileId.trim() || null,
        username: username.trim() || undefined,
        expirationInfo: expirationInfo.trim() || undefined,
        notes: notes.trim() || undefined,
      },
    });

    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-full overflow-y-auto p-0 md:max-w-xl">
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>Edit Inventory</SheetTitle>
            <SheetDescription>
              Update inventory notes, credentials, and operational status in place.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-6 py-6 space-y-4">
          <div className="space-y-1">
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Inventory Status</Label>
              <Select
                value={status}
                onValueChange={(value) => setStatus(value as typeof status)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="depleted">Depleted</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Account Status</Label>
              <Select
                value={manualAccountStatus}
                onValueChange={(value) =>
                  setManualAccountStatus(value as ManualAccountStatus)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              {inventory && (
                <p className="text-xs text-muted-foreground">
                  Effective: {formatStatusLabel(inventory.accountStatus)}
                  {inventory.accountValue != null
                    ? ` · Account value ${formatCurrency({ value: inventory.accountValue })}`
                    : ""}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Owner</Label>
              <SearchableSelect
                value={ownerVendorId}
                onChange={handleOwnerVendorChange}
                placeholder={accountOwnerName || "Select vendor"}
                searchPlaceholder="Search vendor..."
                emptyText="No vendor found."
                options={vendorOptions}
              />
            </div>
            <div className="space-y-1">
              <Label>2FA Status</Label>
              <Select
                value={twoFaStatus}
                onValueChange={(value) =>
                  setTwoFaStatus(value as typeof twoFaStatus)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="email_forwarding">Email Forwarding</SelectItem>
                  <SelectItem value="otp_login">OTP - Login</SelectItem>
                  <SelectItem value="otp_booking">OTP - Booking</SelectItem>
                  <SelectItem value="otp_authenticator">OTP - Authenticator</SelectItem>
                  <SelectItem value="ah_to_book">AH to Book</SelectItem>
                  <SelectItem value="no_need_to_check">No Need to Check</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Email</Label>
              <Input
                value={accountEmail}
                onChange={(event) => setAccountEmail(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Password</Label>
              <Input
                type="password"
                value={accountPassword}
                onChange={(event) => setAccountPassword(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>PayPal Email</Label>
              <Input
                value={paypalEmail}
                onChange={(event) => setPaypalEmail(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>IP Location</Label>
              <Input
                value={ipLocation}
                onChange={(event) => setIpLocation(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <Label>Status Tier</Label>
              <Input
                value={statusTierName}
                onChange={(event) => setStatusTierName(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Status Tier %</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={statusTierPercentage}
                onChange={(event) => setStatusTierPercentage(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>DOB</Label>
              <Input
                type="date"
                value={dob}
                onChange={(event) => setDob(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Card Type Tag</Label>
              <Input
                value={cardTypeTag}
                onChange={(event) => setCardTypeTag(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>GoLogin Profile Id</Label>
              <Input
                value={gologinProfileId}
                onChange={(event) => setGologinProfileId(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>User ID / Username</Label>
            <Input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="Account username or member ID"
            />
          </div>

          <div className="space-y-1">
            <Label>Expiration Info</Label>
            <Textarea
              value={expirationInfo}
              onChange={(event) => setExpirationInfo(event.target.value)}
              placeholder="Expiration details, renewal dates, or related notes"
            />
          </div>

          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={!inventory}
              loading={updateInventoryMutation.isPending}
              loadingText="Saving..."
            >
              Save Inventory
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function InventoryWorkspaceStockCheckSheet(
  props: Readonly<
    BaseSheetProps & {
      inventory: Inventory | null | undefined;
    }
  >,
) {
  const { open, onOpenChange, inventory } = props;
  const recordStockCheckMutation = useRecordStockCheck();
  const [reportedBalance, setReportedBalance] = useState("");
  const [notes, setNotes] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [threshold, setThreshold] = useState("5000");

  useEffect(() => {
    if (inventory) {
      setReportedBalance(
        inventory.accountBalance != null
          ? String(inventory.accountBalance)
          : "",
      );
    }
  }, [inventory]);

  const canSubmit =
    Boolean(inventory?.id) &&
    reportedBalance.trim() !== "" &&
    Number.isFinite(Number(reportedBalance));

  const previewVariance =
    inventory && reportedBalance.trim() !== "" && Number.isFinite(Number(reportedBalance))
      ? Number(reportedBalance) - inventory.availableMiles
      : null;

  const handleSubmit = async () => {
    if (!inventory || !canSubmit) {
      return;
    }

    await recordStockCheckMutation.mutateAsync({
      inventoryId: inventory.id,
      reportedBalance: Number(reportedBalance),
      notes: notes.trim() || undefined,
      attachmentUrl: attachmentUrl.trim() || undefined,
      autoBlockOnVarianceThreshold: threshold
        ? Number(threshold)
        : undefined,
    });

    setNotes("");
    setAttachmentUrl("");
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-full overflow-y-auto p-0 md:max-w-lg">
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>Record Stock Check</SheetTitle>
            <SheetDescription>
              Record a balance check directly against this inventory.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-6 py-6 space-y-4">
          <div className="space-y-1">
            <Label>Available</Label>
            <Input value={String(inventory?.availableMiles || 0)} disabled />
          </div>

          <div className="space-y-1">
            <Label>Account Balance</Label>
            <Input
              value={reportedBalance}
              onChange={(event) => setReportedBalance(event.target.value)}
              placeholder="Reported balance from the account"
            />
            {previewVariance != null ? (
              <p className="text-xs text-muted-foreground">
                Variance: {previewVariance.toLocaleString()}
              </p>
            ) : null}
          </div>

          <div className="space-y-1">
            <Label>Auto-block threshold</Label>
            <Input
              value={threshold}
              onChange={(event) => setThreshold(event.target.value)}
              placeholder="5000"
            />
          </div>

          <div className="space-y-1">
            <Label>Attachment URL</Label>
            <Input
              value={attachmentUrl}
              onChange={(event) => setAttachmentUrl(event.target.value)}
              placeholder="Optional evidence link"
            />
          </div>

          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Variance notes or follow-up context"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={recordStockCheckMutation.isPending}
              loadingText="Recording..."
            >
              Record Stock Check
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
